import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch, useAppSelector } from '@/store';
import { UPLOAD_CONTENT } from '@/features/content/graphql/queries';
import { 
  addUpload, 
  updateUploadProgress, 
  completeUpload, 
  failUpload, 
  removeUpload 
} from '@/features/content/contentSlice';
import { UploadContentResponse, Content, ContentGraphQLErrorClass, ContentType } from '@/features/content/types';
import { LoadingStatus } from '@/types';
import { toast } from 'react-toastify';


// Helper function to map API response to Content
const mapResponseToContent = (response: UploadContentResponse): Content => {
  const contentData = response.content;
  return {
    id: contentData.id,
    fileName: contentData.file_name,
    mimeType: contentData.mime_type,
    size: contentData.size,
    processingStatus: contentData.processing_status as any,
    downloadUrl: contentData.download_url,
    createdAt: contentData.created_at,
    updatedAt: contentData.updated_at,
    type: getContentTypeFromMimeType(contentData.mime_type),
    userId: contentData.user.id
  };
};

// Helper function to determine content type from MIME type
const getContentTypeFromMimeType = (mimeType: string): ContentType => {
  if (mimeType.startsWith('image/')) return ContentType.IMAGE;
  if (mimeType.startsWith('video/')) return ContentType.VIDEO;
  if (mimeType.startsWith('audio/')) return ContentType.AUDIO;
  return ContentType.DOCUMENT;
};

// Generate unique upload ID
const generateUploadId = () => `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const useContentUploader = () => {
  const dispatch = useAppDispatch();
  const { uploads } = useAppSelector((state) => state.content);
  
  const [uploadMutation] = useMutation(UPLOAD_CONTENT);
  
  // Start upload for a single file
  const startUpload = useCallback(async (file: File) => {
    const uploadId = generateUploadId();
    
    // Add to upload queue
    dispatch(addUpload({ id: uploadId, file }));
    
    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        const currentUpload = uploads.find(u => u.id === uploadId);
        if (currentUpload && currentUpload.progress < 90) {
          const newProgress = Math.min(90, currentUpload.progress + Math.random() * 15);
          dispatch(updateUploadProgress({ id: uploadId, progress: newProgress }));
        }
      }, 500);
      
      // Perform the actual upload
      const result = await uploadMutation({
        variables: { file }
      });
      
      // Clear progress interval
      clearInterval(progressInterval);
      
      if (result.data?.uploadContent) {
        const content = mapResponseToContent(result.data.uploadContent);
        dispatch(completeUpload({ id: uploadId, content }));
        toast.success(`${file.name} uploaded successfully!`);
        return { success: true, uploadId, content };
      } else {
        throw new Error('Upload failed - no data returned');
      }
      
    } catch (error: any) {
      // Handle GraphQL errors
      const graphQLErrors = error.graphQLErrors || [];
      let errorMessage = 'Upload failed';
      
      for (const err of graphQLErrors) {
        const extensions = err.extensions || {};
        const className = extensions.class_name as string;
        
        switch (className) {
          case ContentGraphQLErrorClass.FILE_SIZE_EXCEEDED:
            errorMessage = 'File size exceeded';
            break;
          case ContentGraphQLErrorClass.INVALID_FILE_TYPE:
            errorMessage = 'Invalid file type';
            break;
          case ContentGraphQLErrorClass.UPLOAD_FAILED:
            errorMessage = 'Upload failed';
            break;
          default:
            errorMessage = err.message || 'Upload failed';
        }
      }
      
      dispatch(failUpload({ id: uploadId, error: errorMessage }));
      toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
      return { success: false, uploadId, error: errorMessage };
    }
  }, [dispatch, uploadMutation, uploads]);
  
  // Upload multiple files
  const uploadFiles = useCallback(async (files: File[]) => {
    const results = await Promise.allSettled(
      files.map(file => startUpload(file))
    );
    
    const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
    const failed = results.length - successful;
    
    if (successful > 0) {
      toast.success(`${successful} file(s) uploaded successfully!`);
    }
    if (failed > 0) {
      toast.error(`${failed} file(s) failed to upload`);
    }
    
    return results;
  }, [startUpload]);
  
  // Cancel upload
  const cancelUpload = useCallback((uploadId: string) => {
    dispatch(removeUpload(uploadId));
    toast.info('Upload cancelled');
  }, [dispatch]);
  
  // Retry failed upload
  const retryUpload = useCallback(async (uploadId: string) => {
    const upload = uploads.find(u => u.id === uploadId);
    if (upload && upload.status === LoadingStatus.FAILED) {
      // Remove the failed upload and start a new one
      dispatch(removeUpload(uploadId));
      return await startUpload(upload.file);
    }
  }, [uploads, dispatch, startUpload]);
  
  // Remove upload from queue
  const removeUploadFromQueue = useCallback((uploadId: string) => {
    dispatch(removeUpload(uploadId));
  }, [dispatch]);
  
  // Get active uploads (not completed or failed)
  const activeUploads = uploads.filter(u => u.status === LoadingStatus.LOADING);
  
  // Get completed uploads
  const completedUploads = uploads.filter(u => u.status === LoadingStatus.SUCCEEDED);
  
  // Get failed uploads
  const failedUploads = uploads.filter(u => u.status === LoadingStatus.FAILED);
  
  // Trigger file picker
  const triggerFilePicker = useCallback(() => {
    // This will be handled by the ContentUploader component
    const event = new CustomEvent('triggerFilePicker');
    window.dispatchEvent(event);
  }, []);

  return {
    uploads,
    activeUploads,
    completedUploads,
    failedUploads,
    startUpload,
    uploadFiles,
    cancelUpload,
    retryUpload,
    removeUploadFromQueue,
    triggerFilePicker,
    isUploading: activeUploads.length > 0
  };
}; 